<template>
  <inertia-link
    data-cy="enter-session-details"
    :href="route('session.user.details', sessionDetails.session.unique_id)"
    class="
      block
      shadow
      mt-8
      border border-vice-light-grey
      overflow-hidden
      sm:rounded-lg
      hover:bg-gray-150
    "
  >
    <div class="px-4 pt-4 pb-5 sm:pl-6 sm:pr-4 border-b">
      <div class="flex flex-col md:flex-row justify-between gap-3">
        <h3 class="flex-grow text-xl leading-7 mb-3 font-medium text-vice-teal-900">
          {{ sessionDetails.session.title }}
        </h3>
        <div class="flex-none flex md:justify-end items-start mb-3 md:mb-0">
          <template v-if="!sessionDetails.user_type">
            <ViceBadge red icon="MailIcon">Pending invitation</ViceBadge>
          </template>
          <template v-else>
            <ViceBadge v-if="sessionDetails.user_type == 'participant'" indigo icon="UserIcon">
              {{ sessionDetails.user_type }}
            </ViceBadge>

            <ViceBadge v-else yellow icon="EyeIcon">
              {{ sessionDetails.user_type }}
            </ViceBadge>
          </template>
        </div>
      </div>

      <div class="flex flex-col items-start md:flex-row gap-4 mt-0.5">
        <div class="flex items-start gap-4">
          <p class="flex gap-1">
            <CalendarIcon class="h-5 w-5 text-gray-300"></CalendarIcon>
            <span class="text-gray-900 text-xs">
              {{ yearFormatter(sessionDetails.session.details.session_start_by_timezone) }}
            </span>
          </p>
          <p class="flex gap-1">
            <ClockIcon class="h-5 w-5 text-gray-300"></ClockIcon>
            <span class="text-gray-900 text-xs">
              {{ hoursFormatter(sessionDetails.session.details.session_start_by_timezone) }}
            </span>
          </p>
        </div>
      </div>
    </div>
    <div class="px-4 py-4 bg-white sm:px-6 flex">
      <div class="flex-shrink-0 group block">
        <div class="flex-shrink-0 group block">
          <div class="flex items-center">
            <div>
              <ViceAvatar
                :image="sessionDetails.session.details?.clinician?.profile_photo_url"
                :name="sessionDetails.session.details?.clinician?.name"
                class="w-8 h-8"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="ml-3">
        <p class="text-sm font-medium text-gray-700 group-hover:text-gray-900">
          {{
            (sessionDetails.session.details.clinician?.details?.title
              ? sessionDetails.session.details.clinician?.details?.title + ' '
              : '') + sessionDetails.session.details.clinician?.name
          }}
        </p>
        <p
          v-if="sessionDetails.session.details.clinician?.details"
          class="text-xs font-medium text-gray-500 group-hover:text-gray-700"
        >
          {{ sessionDetails.session.details.clinician.details.position }}
        </p>
      </div>
      <div class="flex flex-grow items-center justify-end">
        <GroupOfPeopleIcon v-if="!sessionDetails.session.is_private" />
        <LockIcon v-if="sessionDetails.session.is_private" />
        <p class="ml-1.75 text-sm font-medium text-vice-teal-900 group-hover:text-gray-800">
          {{ sessionDetails.session.is_private ? 'Private' : 'Public' }}
        </p>
      </div>
    </div>
  </inertia-link>
</template>

<script>
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

import { CalendarIcon, ClockIcon } from '@heroicons/vue/solid';
import DateFormatMixin from '@/Mixins/DateFormatMixin.vue';
import ViceBadge from '@/Components/Badges/ViceBadge.vue';
import ViceAvatar from '@/Components/Avatars/ViceAvatar.vue';
import GroupOfPeopleIcon from '@/Components/Icons/GroupOfPeopleIcon.vue';
import LockIcon from '@/Components/Icons/LockIcon.vue';

dayjs.extend(duration);
dayjs.extend(relativeTime);
export default {
  name: 'CardUserSession',
  props: {
    sessionDetails: {
      required: true,
    },
  },
  mixins: [DateFormatMixin],
  components: {
    LockIcon,
    GroupOfPeopleIcon,
    ViceBadge,
    CalendarIcon,
    ClockIcon,
    ViceAvatar,
  },
  data() {
    return {
      sessionStartsIn: '',
    };
  },
  mounted() {
    this.sessionStartsIn = this.updateSessionStartTime();
    setInterval(() => {
      this.sessionStartsIn = this.updateSessionStartTime();
    }, 10000);
  },
  methods: {
    updateSessionStartTime() {
      const now = dayjs(); // todays date
      const end = dayjs(this.sessionDetails.session.details.session_start_by_timezone);

      const durationMoment = dayjs.duration(end.diff(now));

      const seconds = durationMoment.asSeconds();
      if (seconds <= 0) return 'Already has started!';
      if (seconds > 3600 * 24) return null;
      return `Starts in ${dayjs.duration(seconds, 'seconds').humanize()}`;
    },
  },
};
</script>

<style scoped>
.min-w-\[160px\] {
  min-width: 160px;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
